import React, { useRef } from "react";
import { TextField } from "@mui/material";
import { Formik, Form } from "formik";
import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";
import { usePatientNoteRequestToDeleteMutation } from "app/store/patient/api/patientApi";
import { showErrorToast, showSuccessToast } from "utils/toast";
import useNotificationSocket from "containers/AppPage/Header/Notification/useNotificationSocket";

const RequestDeleteNoteModal = ({
  show,
  note,
  isSubmitting = false,
  onCancel,
}) => {
  const { patientId, noteId } = note || {};
  const { emitNewNotification } = useNotificationSocket();

  const renderFormHeadingContent = () => {
    const textStyle = {
      color: "#444444",
      fontSize: "16px",
      fontWeight: 400,
      fontFamily: "Roboto",
    };

    const boldTextStyle = {
      ...textStyle,
      fontWeight: 700,
    };

    const addedOnStyle = {
      ...textStyle,
      color: "#949494",
    };

    const contentStyle = {
      ...textStyle,
      marginBottom: "30px",
    };

    const wrapperStyle = {
      marginTop: "12px",
      marginBottom: "20px",
    };

    return (
      <div style={wrapperStyle}>
        <div style={boldTextStyle}>{note?.type || "Manual Note"}</div>
        <div style={contentStyle}>{note?.content || "-"}</div>
        <div style={addedOnStyle} className="mt-2">
          Added on: {note?.date} {note?.time}{" "}
          {note?.addedBy ? `by ${note?.addedBy}` : ""}
        </div>
      </div>
    );
  };
  const formikRef = useRef(null);

  const [patientNoteRequestToDelete] = usePatientNoteRequestToDeleteMutation();

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!noteId) {
      return;
    }

    try {
      const response = await patientNoteRequestToDelete({
        patientId,
        noteId,
        reason: values.reason.trim(),
      }).unwrap();

      if (!response?.error) {
        showSuccessToast(response?.message);
        const notification = response?.data?.notification;

        if (notification) {
          const userIds = notification?.recipients.map(
            (recipient) => recipient?.user?._id
          );

          emitNewNotification(userIds);
        }
        resetForm();
        onCancel();
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    formikRef.current?.submitForm();
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        reason: "",
      }}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting: formikSubmitting,
      }) => (
        <Form>
          <ConfirmModal
            title="Request to Delete Notes"
            show={show}
            isConfirming={isSubmitting || formikSubmitting}
            onConfirm={handleConfirm}
            onCancel={onCancel}
            confirmText="Send Request"
            cancelText="Cancel"
            confirmButtonProps={{
              color: "secondary",
              disabled: !values.reason.trim() || !noteId,
            }}
            cancelButtonProps={{
              variant: "outlined",
              color: "secondary",
            }}
          >
            <div>
              {renderFormHeadingContent()}
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                name="reason"
                label="Reason"
                placeholder="Add Reason for deleting"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                inputProps={{ maxLength: 500 }}
              />
            </div>
          </ConfirmModal>
        </Form>
      )}
    </Formik>
  );
};

export default RequestDeleteNoteModal;
